import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ShieldCheck, BadgeCheck, Sparkles, BrainCircuit,
  LineChart, Compass, Leaf, Flame, Award, ChevronRight, Gift
} from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { FAQ, Disclaimer } from "../components/FAQ";
import { useAuth } from "../lib/auth";
import { BRAND, PRODUCT_IMAGES } from "../lib/brand";

const heroTexture =
  "https://images.unsplash.com/photo-1518998053901-5348d3961a04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Landing() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const navigate = useNavigate();
  const startAssessment = () => navigate(user ? "/test" : "/auth?mode=signup");
  const exploreShop = () => navigate("/shop/wellness");

  return (
    <div className="min-h-screen bg-background relative">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden brand-gradient-bg">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: `url(${heroTexture})`, backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              <span className="text-xs font-semibold text-primary tracking-widest uppercase">
                {isHi ? "अनंत का सार" : "Essence of the Infinite"}
              </span>
            </div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[3.8rem] font-extrabold tracking-tight leading-[1.02]">
              {isHi ? (
                <>अपनी <span className="relative inline-block">
                  <span className="text-primary">अनंत क्षमता</span>
                  <span className="absolute -bottom-1.5 left-0 right-0 h-3 bg-accent/30 -z-10 rounded-sm"></span>
                </span> को पहचानिए।</>
              ) : (
                <>Discover your <span className="relative inline-block">
                  <span className="text-primary">Infinite Potential</span>
                  <span className="absolute -bottom-1.5 left-0 right-0 h-3 bg-accent/30 -z-10 rounded-sm"></span>
                </span>.</>
              )}
            </h1>
            <div className="mt-6 max-w-xl">
              {isHi ? (
                <>
                  <p className="text-lg text-foreground/80 leading-relaxed">स्वयं को जानिए।</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">बेहतर आदतें बनाइए।</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">अपने स्वास्थ्य और आत्मविश्वास को विकसित कीजिए।</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">भविष्य के लिए स्वयं को तैयार कीजिए।</p>
                </>
              ) : (
                <>
                  <p className="text-lg text-foreground/80 leading-relaxed">Know yourself.</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">Build better habits.</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">Improve your well-being.</p>
                  <p className="text-lg text-foreground/80 leading-relaxed">Develop future-ready skills.</p>
                </>
              )}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button onClick={startAssessment} data-testid="hero-start-btn" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-base font-semibold">
                {isHi ? "BharatIQ मूल्यांकन शुरू करें" : "Start BharatIQ Assessment"} <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={exploreShop} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-base font-semibold border border-primary/20 text-primary hover:bg-primary/5 transition-colors" data-testid="hero-shop-btn">
                {isHi ? "VyomSutra देखें" : "Explore VyomSutra"}
              </button>
            </div>

            {/* Reward banner */}
            <div className="mt-8 inline-flex items-center gap-3 rounded-lg border border-accent/40 bg-accent/8 px-4 py-3">
              <Gift className="w-5 h-5 text-accent flex-shrink-0" />
              <div className="text-sm">
                <span className="font-semibold text-primary">
                  {isHi ? "स्वागत उपहार:" : "Welcome Reward:"}
                </span>{" "}
                <span className="text-foreground/80">
                  {isHi
                    ? "प्रत्येक भुगतान किए गए BharatIQ मूल्यांकन के साथ 50% छूट कूपन।"
                    : "50% OFF coupon with every paid BharatIQ assessment."}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-5 relative">
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-border shadow-[0_30px_80px_-20px_rgba(10,25,47,0.28)] bg-white">
              <img src={BRAND.logoUrl} alt="VyomSutra" className="absolute inset-0 w-full h-full object-cover opacity-95" />
            </div>
            <div className="hidden md:flex absolute -left-3 top-6 glass rounded-md px-3 py-2 items-center gap-2 shadow-sm">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Trusted · Secure</span>
            </div>
            <div className="hidden md:flex absolute -right-3 bottom-6 glass rounded-md px-3 py-2 items-center gap-2 shadow-sm">
              <BadgeCheck className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Bilingual · EN · हिन्दी</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUSTBAR */}
      <section className="border-y border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-primary" /> Big Five · RIASEC · EQ frameworks</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> 256-bit encrypted</span>
          <span className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> 12,400+ Indians assessed</span>
        </div>
      </section>

      {/* TRANSFORMATION — 6 STEPS */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="label-eyebrow mb-3">{isHi ? "आपकी परिवर्तन यात्रा" : "Your transformation journey"}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {isHi ? "छह चरण। एक जीवन बदलने वाला रास्ता।" : "Six steps. One life-changing path."}
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Compass, en: "Discover Yourself", hi: "स्वयं को खोजिए", num: "01" },
              { icon: BrainCircuit, en: "Understand Yourself", hi: "स्वयं को समझिए", num: "02" },
              { icon: LineChart, en: "Build Daily Rituals", hi: "दैनिक अनुष्ठान बनाइए", num: "03" },
              { icon: Flame, en: "Boost Energy", hi: "ऊर्जा बढ़ाइए", num: "04" },
              { icon: Sparkles, en: "Build Confidence", hi: "आत्मविश्वास बढ़ाइए", num: "05" },
              { icon: Award, en: "Become Future-Ready", hi: "भविष्य के लिए तैयार बनिए", num: "06" },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-xl border border-border p-6 bg-white hover:border-primary/40 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-md bg-primary/5 grid place-items-center">
                    <s.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-display text-xs font-extrabold tracking-widest text-muted-foreground">{s.num}</span>
                </div>
                <div className="mt-5 font-display text-xl font-bold">{isHi ? s.hi : s.en}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM: 3 PILLARS */}
      <section className="py-20 lg:py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="max-w-2xl">
            <div className="label-eyebrow text-white/60 mb-3">{isHi ? "इकोसिस्टम" : "The Ecosystem"}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {isHi ? "तीन स्तंभ। एक यात्रा।" : "Three pillars. One journey."}
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                title: "BharatIQ",
                sub: isHi ? "स्वयं की खोज" : "Self-Discovery",
                desc: isHi
                  ? "IQ, व्यक्तित्व, EQ और करियर बुद्धिमत्ता — विज्ञान द्वारा समर्थित।"
                  : "IQ, personality, EQ, and career intelligence — grounded in science.",
                cta: isHi ? "मूल्यांकन शुरू करें" : "Start assessment",
                to: user ? "/test" : "/auth?mode=signup",
                icon: BrainCircuit,
              },
              {
                title: "VyomSutra Wellness",
                sub: isHi ? "दैनिक अनुष्ठान" : "Daily rituals",
                desc: isHi
                  ? "शुद्ध हिमालयन शिलाजीत और भविष्य के वेलनेस उत्पाद — स्वस्थ दिनचर्या के लिए।"
                  : "Pure Himalayan Shilajit and future wellness products — for healthy routines.",
                cta: isHi ? "देखिए" : "Shop wellness",
                to: "/shop/wellness",
                icon: Leaf,
              },
              {
                title: "VyomSutra Aura",
                sub: isHi ? "उपस्थिति और पहचान" : "Presence & identity",
                desc: isHi
                  ? "अत्तर, परफ्यूम और बॉडी मिस्ट — आत्मविश्वास और उपस्थिति के लिए।"
                  : "Attars, perfumes, and body mists — for confidence and presence.",
                cta: isHi ? "देखिए" : "Explore Aura",
                to: "/shop/aura",
                icon: Sparkles,
              },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-xl border border-white/15 p-7 bg-white/5 backdrop-blur-sm"
              >
                <p.icon className="w-6 h-6 text-accent" />
                <div className="mt-4 font-display text-2xl font-extrabold">{p.title}</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{p.sub}</div>
                <p className="mt-4 text-sm text-white/80 leading-relaxed min-h-[60px]">{p.desc}</p>
                <Link to={p.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover-underline">
                  {p.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT — SHILAJIT */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="label-eyebrow mb-3">{isHi ? "बेस्टसेलर" : "Bestseller"}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {isHi ? "शुद्ध हिमालयन शिलाजीत" : "Pure Himalayan Shilajit"}
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-lg">
              {isHi
                ? "15,000 फीट से अधिक ऊँचाई से — पारंपरिक रूप से सूर्य-सुखाया, अप्रसंस्कृत रेज़िन। दैनिक अनुष्ठान के लिए 20g।"
                : "Sourced from altitudes above 15,000 ft — traditionally sun-dried, unprocessed resin. 20g for your daily ritual."}
            </p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-4xl font-extrabold text-primary">₹1,499</span>
              <span className="text-sm text-muted-foreground line-through">₹1,999</span>
              <span className="text-xs font-bold text-accent px-2 py-0.5 bg-accent/10 rounded">25% OFF</span>
            </div>
            <ul className="mt-6 space-y-2 text-sm">
              {[
                isHi ? "20g शुद्ध रेज़िन, धूप में सुखाया, अप्रसंस्कृत" : "20g pure resin, sun-dried, unprocessed",
                isHi ? "फुलविक और ह्यूमिक अम्ल से भरपूर" : "Rich in fulvic and humic acids",
                isHi ? "एक चावल-दाने की खुराक गर्म पानी या दूध में" : "One rice-grain sized dose in warm water or milk",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <ChevronRight className="w-4 h-4 text-accent flex-shrink-0" /> <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/products/pure-shilajit-20g" className="mt-7 inline-flex btn-primary items-center gap-2 px-6 py-3 rounded-md font-semibold" data-testid="landing-shilajit-cta">
              {isHi ? "अभी देखें" : "View product"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 aspect-[16/10] rounded-xl overflow-hidden border border-border bg-white">
                <img src={PRODUCT_IMAGES.shilajit_pour} alt="Pure Shilajit resin pouring" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden border border-border bg-white">
                <img src={PRODUCT_IMAGES.shilajit_drink} alt="Shilajit ritual in cup" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden border border-border bg-white">
                <img src={PRODUCT_IMAGES.shilajit_hero} alt="Pure Shilajit jar" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE / CONTENT PRINCIPLES */}
      <section className="py-20 bg-muted/40 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="label-eyebrow mb-3">{isHi ? "हमारा वादा" : "Our promise"}</div>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
            {isHi
              ? "एक परीक्षा। एक उत्पाद। यह पर्याप्त नहीं है।"
              : "One test. One product. That is not enough."}
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {isHi
              ? "BharatIQ आपको स्वयं को समझने की अंतर्दृष्टि देता है। VyomSutra आपकी दैनिक दिनचर्या और आत्मविश्वास को सहारा देता है। लगातार सीखना, अनुशासित आदतें, और निरंतर प्रयास — यही एक अर्थपूर्ण जीवन का सूत्र है।"
              : "BharatIQ gives you the insights to understand yourself. VyomSutra supports your daily routines and confidence. Continuous learning, disciplined habits, and consistent effort — that is the sutra of a meaningful life."}
          </p>
        </div>
      </section>

      {/* BHARATIQ SCORE + METHODOLOGY */}
      <section className="py-20 lg:py-24" id="score">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">{isHi ? "BharatIQ Score" : "The BharatIQ Score"}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {isHi ? "एक संख्या। पाँच आयाम। शून्य अनुमान।" : "One number. Five dimensions. Zero guesswork."}
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed">
              {isHi
                ? "0–1000 का प्रोप्राइटरी स्कोर, नियम-आधारित इंजन से — AI से नहीं। AI केवल आपके नंबरों को समझाता है और करियर योजना लिखता है।"
                : "A proprietary 0–1000 score computed by a deterministic, rule-based engine — never by AI. AI only explains your numbers and writes your career plan."}
            </p>
            <div className="mt-6 space-y-3" data-testid="score-dimensions">
              {[
                { en: "Critical Thinking", hi: "क्रिटिकल थिंकिंग", w: 25 },
                { en: "Personality Fit", hi: "व्यक्तित्व फ़िट", w: 20 },
                { en: "Emotional Intelligence", hi: "भावनात्मक बुद्धिमत्ता", w: 20 },
                { en: "Career Alignment", hi: "करियर संरेखण", w: 20 },
                { en: "Learning Readiness", hi: "सीखने की तत्परता", w: 15 },
              ].map((d, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold">{isHi ? d.hi : d.en}</span>
                    <span className="font-mono text-xs text-muted-foreground">{d.w}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${d.w * 4}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.08 }} className="h-full bg-primary" />
                  </div>
                </div>
              ))}
            </div>
            <Link to="/knowledge" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover-underline" data-testid="landing-methodology-link">
              {isHi ? "पूरी कार्यप्रणाली पढ़ें" : "Read the full methodology"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-white p-8 shadow-sm relative overflow-hidden" data-testid="sample-score-card">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-accent/15 blur-3xl" />
              <div className="relative">
                <div className="label-eyebrow">{isHi ? "नमूना रिपोर्ट" : "Sample report"}</div>
                <div className="mt-4 flex items-end gap-3">
                  <span className="font-display text-6xl lg:text-7xl font-extrabold text-primary tracking-tight">782</span>
                  <span className="text-muted-foreground pb-2 text-lg">/1000</span>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{isHi ? "शीर्ष 10% उपयोगकर्ताओं में · Investigative–Enterprising प्रोफ़ाइल" : "Top 10% of users · Investigative–Enterprising profile"}</div>
                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {[
                    { l: isHi ? "शीर्ष करियर" : "Top career", v: "Product Manager", s: "Fit 91" },
                    { l: isHi ? "नेतृत्व क्षमता" : "Leadership potential", v: "84", s: "/100" },
                    { l: isHi ? "विश्लेषण योग्यता" : "Analytical ability", v: "88", s: "/100" },
                  ].map((k, i) => (
                    <div key={i} className="rounded-lg border border-border p-4 bg-muted/30">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{k.l}</div>
                      <div className="mt-1 font-display text-lg font-extrabold text-primary leading-tight">{k.v} <span className="text-xs text-muted-foreground font-medium">{k.s}</span></div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-lg bg-primary text-primary-foreground p-4 text-sm leading-relaxed">
                  {isHi
                    ? "“आपका उच्च खुलापन (81) और कर्तव्यनिष्ठा (76) बताते हैं कि आप तेज़ी से सीखते हैं और काम पूरा करते हैं — प्रोडक्ट और डेटा भूमिकाओं के लिए आदर्श…”"
                    : "“Your high Openness (81) and Conscientiousness (76) describe someone who learns fast and finishes what they start — ideal for product and data roles…”"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ + DISCLAIMER */}
      <section className="py-20 bg-muted/40 border-y border-border" id="faq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3">FAQ</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
              {isHi ? "अक्सर पूछे जाने वाले प्रश्न" : "Questions, answered."}
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {isHi ? "और कुछ पूछना है? हमसे संपर्क करें।" : "Still curious? Reach us any time."}{" "}
              <Link to="/contact" className="text-primary font-semibold hover-underline">{isHi ? "संपर्क" : "Contact"}</Link>
            </p>
            <div className="mt-6"><Disclaimer /></div>
          </div>
          <div className="lg:col-span-8"><FAQ /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl bg-primary text-primary-foreground p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="label-eyebrow text-white/60">{isHi ? "तैयार?" : "Ready?"}</div>
                <h3 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-3">
                  {isHi ? "आज 15 मिनट। अगले 10 साल की स्पष्टता।" : "15 minutes today. Clarity for the next 10 years."}
                </h3>
              </div>
              <div className="flex md:justify-end">
                <button onClick={startAssessment} data-testid="cta-final-btn" className="btn-accent inline-flex items-center gap-2 px-6 py-4 rounded-md font-semibold text-base">
                  {isHi ? "अभी शुरू करें" : "Start now"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
